import { useContext } from 'react';
import { InvoiceContext } from '../components/context/invoiceData';

interface SurchargeConfig {
  allowPartialPayment: boolean;
  amountDue: string;
  surchargeMin: string;
  surchargeMax: string;
  productTransactionKey: 'cc_product_transaction' | 'ach_product_transaction';
}

function roundToTwo(num: number): number {
  return Math.round((num + Number.EPSILON) * 100) / 100;
}

// eslint-disable-next-line import/prefer-default-export
export function useSurchargeCalculation(config: SurchargeConfig) {
  const {
    allowPartialPayment, amountDue, surchargeMin, surchargeMax, productTransactionKey,
  } = config;
  const invoiceContext = useContext(InvoiceContext);

  function getSubtotal(paymentAmount: string): string {
    if (allowPartialPayment) {
      return paymentAmount;
    }
    return amountDue;
  }

  function getSurchargeFee(paymentAmount: string): string {
    const surchargeData = (invoiceContext?.invoicingData as any)?.[productTransactionKey];
    const fee = surchargeData?.surcharge?.surcharge_fee ?? 0;
    const rate = surchargeData?.surcharge?.surcharge_rate ?? 0;
    const subtotal = Number(getSubtotal(paymentAmount));
    const totalSurchargeFee = subtotal * (rate / 100) + +fee;
    const surchargeFeeAmount = roundToTwo(totalSurchargeFee);
    return surchargeFeeAmount.toString();
  }

  function getSurchargeValidation(paymentAmount: string): string {
    const fee = getSurchargeFee(paymentAmount);
    if (Number(fee) < Number(surchargeMin)) {
      return surchargeMin;
    }
    if (Number(fee) > Number(surchargeMax)) {
      return surchargeMax;
    }
    return fee;
  }

  function getTransactionWithSurcharge(paymentAmount: string): string {
    const subtotal = Number(getSubtotal(paymentAmount));
    const full = invoiceContext.surcharge.isSurcharge
      ? subtotal
      : subtotal + Number(getSurchargeValidation(paymentAmount));
    const transaction = full.toFixed(2);
    return transaction.toString();
  }

  function getSurchargeAmount(paymentAmount: string): string {
    if (invoiceContext.surcharge.isSurcharge) {
      return '0';
    }
    return getSurchargeValidation(paymentAmount);
  }

  return {
    getSubtotal,
    getTransactionWithSurcharge,
    getSurchargeAmount,
  };
}
